import React, { useState } from 'react';
import { FiSliders, FiTrash2, FiPlus } from 'react-icons/fi';
import SettingsModal from '../../../clientSettings/SettingsModal';
import { GripIcon } from '../BoardManagement';
import { GovSection } from './governanceData';

interface Props {
    sections: GovSection[];
    // documents per section id, for the "3 documents" counts
    counts: Record<string, number>;
    onClose: () => void;
    onSave: (sections: GovSection[]) => void;
}

// Corporate governance › Manage sections: rename, reorder (drag), delete, add (spec 410:11657)
const ManageSectionsModal: React.FC<Props> = ({ sections, counts, onClose, onSave }) => {
    const [rows, setRows] = useState(sections);
    const [adding, setAdding] = useState('');
    const [dragging, setDragging] = useState<string | null>(null);
    // only the grip starts a drag, so the name inputs stay selectable
    const [armed, setArmed] = useState<string | null>(null);

    const rename = (id: string, name: string) => setRows(xs => xs.map(x => x.id === id ? { ...x, name, label: name } : x));
    const add = () => {
        const name = adding.trim();
        if (!name) return;
        setRows(xs => [...xs, { id: `s${Date.now()}`, name, label: name }]);
        setAdding('');
    };
    const dragOver = (e: React.DragEvent, overId: string) => {
        e.preventDefault();
        if (!dragging || dragging === overId) return;
        setRows(xs => {
            const from = xs.findIndex(x => x.id === dragging);
            const to = xs.findIndex(x => x.id === overId);
            const next = xs.filter(x => x.id !== dragging);
            next.splice(to, 0, xs[from]);
            return next;
        });
    };
    const blank = rows.some(r => !r.name.trim());

    return <SettingsModal icon={<FiSliders />} title="Manage Sections" sub="Sections group documents on the Corporate governance page" onClose={onClose}
        footer={<button type="button" className="btn-primary" disabled={blank} onClick={() => onSave(rows.map(r => ({ ...r, name: r.name.trim(), label: r.label.trim() })))}>Save Sections</button>}>
        <div className="hub-section-list">
            {rows.map(r => {
                const n = counts[r.id] ?? 0;
                return <div key={r.id} className={`hub-section-row${dragging === r.id ? ' dragging' : ''}`} draggable={armed === r.id}
                    onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDragging(r.id); }}
                    onDragOver={e => dragOver(e, r.id)} onDragEnd={() => { setDragging(null); setArmed(null); }}>
                    <span className="hub-grip" title="Drag to reorder" onMouseDown={() => setArmed(r.id)} onMouseUp={() => setArmed(null)}><GripIcon /></span>
                    <input value={r.name} aria-label="Section name" onChange={e => rename(r.id, e.target.value)} />
                    <span className="hub-section-count">{n} {n === 1 ? 'document' : 'documents'}</span>
                    <button type="button" className="hub-icon-btn danger" aria-label={`Delete ${r.name}`} title="Delete section" onClick={() => setRows(xs => xs.filter(x => x.id !== r.id))}><FiTrash2 /></button>
                </div>;
            })}
        </div>
        <div className="hub-section-add">
            <input value={adding} placeholder="New section name, e.g. Constitution" aria-label="New section name"
                onChange={e => setAdding(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }} />
            <button type="button" className="btn-outline with-icon" disabled={!adding.trim()} onClick={add}><FiPlus /> Add</button>
        </div>
        <p className="hint">Deleting a section doesn’t delete its documents — they move to “Other”.</p>
    </SettingsModal>;
};

export default ManageSectionsModal;
